import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { applyLocale, loadLocale, SUPPORTED_LOCALES, type AppLocale } from '@/i18n'
import axiosInstance from '@/services/axiosInstance'
import { useUserStore } from '@/stores/user-store'

// syncLocaleWithMember reconciles this browser with the language stored against the
// member, and is called right after the profile fetch that follows sign-in. The
// member's stored choice wins when there is one; otherwise this browser's choice is
// pushed up, which is how a language picked on the login page - before anyone was
// signed in - reaches the member at all.
//
// It is a plain function, not part of the composable, because the router guard
// calls it and that is not a setup context.
export async function syncLocaleWithMember(serverLocale: unknown): Promise<void> {
  if (serverLocale === 'en' || serverLocale === 'sr') {
    applyLocale(serverLocale)
    return
  }
  try {
    await axiosInstance.put('/api/v1/member/me', { locale: loadLocale() })
  } catch {
    // Signing in must not fail over a language sync. The next sign-in retries it,
    // and the panel keeps rendering in the locally chosen language meanwhile.
  }
}

export function useLanguage() {
  const { locale } = useI18n()
  const userStore = useUserStore()

  const currentLocale = computed<AppLocale>(() => locale.value as AppLocale)

  function setLocale(value: AppLocale) {
    applyLocale(value)
    // The same picker sits on the login page, where nobody is signed in yet and the
    // write would only 401. The choice still persists locally, and reaches the
    // member through syncLocaleWithMember on the next sign-in.
    if (!userStore.isAuthenticated) return
    axiosInstance.put('/api/v1/member/me', { locale: value }).catch(() => {
      // The UI has already switched; a failed write is not worth interrupting it,
      // and axiosInstance has surfaced the error itself.
    })
  }

  return {
    currentLocale,
    locales: SUPPORTED_LOCALES,
    setLocale,
  }
}
